import React, { createContext, useContext, useEffect, useState } from "react";
import { useSessionContext } from "./SessionContext";
import { useTaskWatchingService } from "../services/sse/TaskWatchingService";
import { oids } from "@/configs/Settings";
import { GetTask } from "@/services/fhir/task/GetTask";

interface TaskWatchingContextValue {
  watchTasks: any[];
  refreshTasks: () => void;
  isConnected: boolean;
  error: string | null;
  allTasks: any[];
  /** Atualiza propriedades de uma task no contexto (ex.: status) */
  updateTaskInContext: (taskId: string, changes: Partial<any>) => void;
}

export const TaskWatchingContext = createContext<TaskWatchingContextValue>(
  null!,
);

interface TaskWatchingContextProviderProps {
  children: React.ReactNode;
}

export function TaskWatchingContextProvider({
  children,
}: TaskWatchingContextProviderProps) {
  const [defaultTasks, setDefaultTasks] = useState<any[]>([]);
  const [overridesById, setOverridesById] = useState<Record<string, any>>({});
  const { access, user } = useSessionContext();
  const userCpf = user?.username;
  const isCpfValid = typeof userCpf === "string" && /^\d{11}$/.test(userCpf);
  const userId = isCpfValid ? `${oids.cpf}-${userCpf}` : "";
  const isProfessional = access?.type !== "patient";
  const organizationId = access?.organizationId ?? "";
  const practitionerRoleId =
    isCpfValid && organizationId ? `${organizationId}-${userId}` : "";

  // SSE:
  // - Paciente: mantém comportamento atual (Patient/{cpf})
  // - Profissional: mantém comportamento atual (PractitionerRole/{org-cpf}) e soma Organization/{org}
  const patientSse = useTaskWatchingService({
    resourceType: "Patient",
    resourceId: access?.type === "patient" && isCpfValid ? userId : "",
  });
  const practitionerRoleSse = useTaskWatchingService({
    resourceType: "PractitionerRole",
    resourceId: isProfessional && practitionerRoleId ? practitionerRoleId : "",
  });
  const organizationSse = useTaskWatchingService({
    resourceType: "Organization",
    resourceId: isProfessional && organizationId ? organizationId : "",
  });

  const tasks =
    access?.type === "patient"
      ? patientSse.tasks
      : [...practitionerRoleSse.tasks, ...organizationSse.tasks];
  const error =
    access?.type === "patient"
      ? patientSse.error
      : practitionerRoleSse.error || organizationSse.error;
  const isConnected =
    access?.type === "patient"
      ? patientSse.isConnected
      : Boolean(practitionerRoleSse.isConnected || organizationSse.isConnected);
  const refreshTasks = () => {
    if (access?.type === "patient") {
      patientSse.refreshTasks();
      return;
    }
    practitionerRoleSse.refreshTasks();
    organizationSse.refreshTasks();
  };

  // Mescla tasks iniciais com as do SSE, removendo duplicadas por id
  const mergedMap = new Map<string, any>();
  [...defaultTasks, ...tasks].forEach((t: any) => {
    if (t?.id) mergedMap.set(t.id, t);
  });
  let allTasks = Array.from(mergedMap.values());

  // Aplica overrides locais (ex.: status atualizado via PATCH)
  allTasks = allTasks.map((t: any) =>
    t?.id && overridesById[t.id] ? { ...t, ...overridesById[t.id] } : t,
  );

  useEffect(() => {
    async function fetchTasks() {
      // Limpa as tasks locais antes de buscar novamente (troca de org ou usuário)
      setDefaultTasks([]);
      setOverridesById({});
      // Só realiza a chamada quando CPF válido
      if (!isCpfValid) {
        return;
      }
      // Em visão de profissional, só busca se houver organização válida
      if (access?.type !== "patient" && !organizationId) {
        return;
      }

      if (access?.type === "patient") {
        const tasks = await GetTask({
          subject: `Patient/${userId}`,
        });
        setDefaultTasks(tasks?.entry?.map((task: any) => task.resource) || []);
        return;
      }

      // Profissional:
      // - Mantém busca atual por subject=PractitionerRole/{org-cpf}
      // - Soma busca por owner=Organization/{org} (requisito para visão profissional)
      const [bySubject, byOwner] = await Promise.all([
        GetTask({ subject: `PractitionerRole/${practitionerRoleId}` }),
        GetTask({ owner: `Organization/${organizationId}` }),
      ]);

      const resources = [
        ...(bySubject?.entry?.map((e: any) => e.resource) || []),
        ...(byOwner?.entry?.map((e: any) => e.resource) || []),
      ];

      // Remove duplicadas por id
      const map = new Map<string, any>();
      resources.forEach((t: any) => {
        if (t?.id) map.set(t.id, t);
      });
      setDefaultTasks(Array.from(map.values()));
    }
    fetchTasks();
  }, [organizationId, access?.type, userId, isCpfValid, practitionerRoleId]);

  // Permite atualizar uma task no contexto (usado para refletir PATCH imediato no UI)
  function updateTaskInContext(taskId: string, changes: Partial<any>) {
    if (!taskId) return;
    setOverridesById((prev) => ({
      ...prev,
      [taskId]: { ...(prev[taskId] ?? {}), ...changes },
    }));
  }

  const contextValue: TaskWatchingContextValue = {
    watchTasks: tasks,
    refreshTasks,
    isConnected,
    error,
    allTasks,
    updateTaskInContext,
  };

  return (
    <TaskWatchingContext.Provider value={contextValue}>
      {children}
    </TaskWatchingContext.Provider>
  );
}

export function useTaskWatchingContext() {
  return useContext(TaskWatchingContext);
}
